import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { Button } from '../ui/Button';
import { NumericInput } from '../common/NumericInput';
import { QuickCreateChipSelect } from './QuickCreateChipSelect';
import { QuickCreateField } from './QuickCreateField';
import { QuickCreateHeroName } from './QuickCreateHeroName';
import { useCategorySheet } from '../../features/quickCreate/SheetProviders';
import { useCreateCategoryMutation } from '../../features/expenses/queries';
import { payloadFromCategoryAllocation } from '../../utils/categoryAllocation';
import { parsePtNumber } from '../../utils/money';
import { QUICK_ALLOCATION_OPTIONS } from '../../utils/quickCreateAllocation';
import pt from '../../locales/pt';

export function QuickCategorySheet() {
  const { isOpen, setOpen, close } = useCategorySheet();
  const createCategory = useCreateCategoryMutation();

  const [name, setName] = useState('');
  const [allocation, setAllocation] = useState('essential');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [error, setError] = useState(null);

  const openedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    setName('');
    setAllocation('essential');
    setMonthlyLimit('');
    setError(null);
  }, [isOpen]);

  const canSubmit = Boolean(name.trim()) && !createCategory.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    setError(null);
    const payload = {
      name: name.trim(),
      ...payloadFromCategoryAllocation(allocation),
      color: '#7C9E6F',
    };
    const limit = parsePtNumber(monthlyLimit);
    if (limit && limit > 0) {
      payload.default_monthly_budget = limit.toFixed(2);
    }
    try {
      await createCategory.mutateAsync(payload);
      toast.success(pt.quickCreate.category.createdToast);
      close();
    } catch (err) {
      setError(
        typeof err === 'string'
          ? err
          : err?.detail || err?.name || pt.auth.errors.generic,
      );
    }
  };

  return (
    <BottomSheet open={isOpen} onOpenChange={setOpen}>
      <BottomSheetContent
        title={pt.quickCreate.category.title}
        layout="bottom"
        footer={
          <Button type="button" className="h-11 w-full rounded-xl" onClick={submit} disabled={!canSubmit}>
            {createCategory.isPending ? pt.common.loading : pt.quickCreate.category.submit}
          </Button>
        }
      >
        <div className="flex flex-col gap-3.5 pb-2">
          <QuickCreateHeroName
            label={pt.quickCreate.category.nameLabel}
            placeholder={pt.quickCreate.category.namePlaceholder}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <QuickCreateField label={pt.quickCreate.allocationLabel}>
            <QuickCreateChipSelect
              options={QUICK_ALLOCATION_OPTIONS}
              value={allocation}
              onChange={setAllocation}
              ariaLabel={pt.quickCreate.allocationLabel}
            />
          </QuickCreateField>

          <QuickCreateField
            label={pt.quickCreate.monthlyLimitLabel}
            optional={pt.quickCreate.optional}
          >
            <div className="flex min-h-[46px] items-center rounded-xl border border-border bg-card px-3.5">
              <NumericInput
                value={monthlyLimit}
                onChange={(event) => setMonthlyLimit(event.target.value)}
                placeholder="€ 0"
                className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
              />
            </div>
          </QuickCreateField>

          {error ? (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
